import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  InputAdornment,
  Paper,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
  Chip,
  CircularProgress,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import EventIcon from '@mui/icons-material/Event';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useNavigate } from 'react-router-dom';
import SearchService, { GlobalSearchResponse } from '../services/SearchService';

const GlobalSearch: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(async () => {
      if (query.trim().length >= 2) {
        setLoading(true);
        try {
          const data = await SearchService.globalSearch(query);
          setResults(data);
          setOpen(true);
        } catch (error) {
          console.error('Search error:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setResults(null);
        setOpen(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [query]);

  const handleClear = () => {
    setQuery('');
    setResults(null);
    setOpen(false);
  };

  const handleCoachClick = (coachId: string) => {
    navigate(`/coaches/${coachId}`);
    handleClear();
  };

  const handleStudentClick = (studentId: string) => {
    navigate(`/students/${studentId}`);
    handleClear();
  };

  const handleSessionClick = () => {
    navigate('/sessions');
    handleClear();
  };

  const handleRequestClick = () => {
    navigate('/requests');
    handleClear();
  };

  const getTotalResults = () => {
    if (!results) return 0;
    return (
      (results.coaches?.length || 0) +
      (results.students?.length || 0) +
      (results.sessions?.length || 0) +
      (results.requests?.length || 0)
    );
  };

  return (
    <Box ref={searchRef} sx={{ position: 'relative', width: { xs: '100%', sm: 350, md: 400 } }}>
      <TextField
        fullWidth
        size="small"
        placeholder="Search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#B0B8C1', fontSize: 18 }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {loading ? (
                <CircularProgress size={16} sx={{ padding: '2px' }} />
              ) : query ? (
                <IconButton size="small" onClick={handleClear} sx={{ padding: '2px', width: 20, height: 20 }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              ) : (
                <Box
                  sx={{
                    backgroundColor: '#F0F1F3',
                    borderRadius: '6px',
                    padding: '2px 8px',
                    fontSize: '11px',
                    color: '#A0A8B3',
                    fontWeight: 500,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Ctrl / ⌘ + K
                </Box>
              )}
            </InputAdornment>
          ),
        }}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '10px',
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            padding: '5px 10px',
            minHeight: 38,
            '& fieldset': {
              borderColor: '#E8EAED',
              borderWidth: '1px',
            },
            '&:hover fieldset': {
              borderColor: '#E8EAED',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#E8EAED',
              borderWidth: '1px',
            },
          },
          '& .MuiOutlinedInput-input': {
            fontSize: '14px',
            color: '#202124',
            padding: '0 !important',
            '&::placeholder': {
              color: '#9AA0A6',
              opacity: 1,
            },
          },
        }}
      />

      {open && results && getTotalResults() > 0 && (
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 1,
            maxHeight: 500,
            overflow: 'auto',
            zIndex: 1300,
          }}
        >
          {/* Coaches */}
          {results.coaches && results.coaches.length > 0 && (
            <>
              <Box sx={{ px: 2, py: 1, backgroundColor: '#f5f5f5' }}>
                <Typography variant="subtitle2" color="textSecondary">
                  <PersonIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Coaches ({results.coaches.length})
                </Typography>
              </Box>
              <List dense>
                {results.coaches.map((coach) => (
                  <ListItemButton
                    key={coach.id}
                    
                    onClick={() => handleCoachClick(coach.id)}
                  >
                    <ListItemAvatar>
                      <Avatar src={coach.avatar}>{coach.name[0]}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={coach.name}
                      secondary={
                        <>
                          {coach.area_of_expertise && (
                            <Chip
                              label={coach.area_of_expertise}
                              size="small"
                              sx={{ mr: 1, height: 20 }}
                            />
                          )}
                          {coach.email}
                        </>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}

          {/* Students */}
          {results.students && results.students.length > 0 && (
            <>
              <Box sx={{ px: 2, py: 1, backgroundColor: '#f5f5f5' }}>
                <Typography variant="subtitle2" color="textSecondary">
                  <SchoolIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Students ({results.students.length})
                </Typography>
              </Box>
              <List dense>
                {results.students.map((student) => (
                  <ListItemButton
                    key={student.id}
                    
                    onClick={() => handleStudentClick(student.id)}
                  >
                    <ListItemAvatar>
                      <Avatar src={student.avatar}>{student.name[0]}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={student.name}
                      secondary={
                        <>
                          {student.focus_area && (
                            <Chip
                              label={student.focus_area}
                              size="small"
                              sx={{ mr: 1, height: 20 }}
                            />
                          )}
                          {student.email}
                        </>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}

          {/* Sessions */}
          {results.sessions && results.sessions.length > 0 && (
            <>
              <Box sx={{ px: 2, py: 1, backgroundColor: '#f5f5f5' }}>
                <Typography variant="subtitle2" color="textSecondary">
                  <EventIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Sessions ({results.sessions.length})
                </Typography>
              </Box>
              <List dense>
                {results.sessions.map((session) => (
                  <ListItemButton key={session.id} onClick={handleSessionClick}>
                    <ListItemText
                      primary={session.title || 'Session'}
                      secondary={`${session.coach_name} ↔ ${session.student_name} • ${session.status}`}
                    />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}

          {/* Requests */}
          {results.requests && results.requests.length > 0 && (
            <>
              <Box sx={{ px: 2, py: 1, backgroundColor: '#f5f5f5' }}>
                <Typography variant="subtitle2" color="textSecondary">
                  <AssignmentIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Requests ({results.requests.length})
                </Typography>
              </Box>
              <List dense>
                {results.requests.map((request) => (
                  <ListItemButton key={request.id} onClick={handleRequestClick}>
                    <ListItemText
                      primary={request.student_name}
                      secondary={
                        <>
                          <Chip
                            label={request.status}
                            size="small"
                            color={request.status === 'pending' ? 'warning' : 'default'}
                            sx={{ mr: 1, height: 20 }}
                          />
                          {request.coach_name || 'Unassigned'}
                        </>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
        </Paper>
      )}

      {open && results && getTotalResults() === 0 && (
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 1,
            p: 3,
            zIndex: 1300,
            textAlign: 'center',
          }}
        >
          <Typography color="textSecondary">No results found for "{query}"</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default GlobalSearch;
